import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Box,
  Typography,
  TextField,
  Button,
  Checkbox,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useTheme,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButton,
  ToggleButtonGroup,
  styled,
} from '@mui/material';
import api from '../../API/api';
import { fetchSpotBalances } from '../../store/Slices/assetSlice';
import BalanceSlider from './BalanceSlider';

// Binance-inspired colors
const COLORS = {
  YELLOW: '#F0B90B',
  GREEN: '#00A478',
  RED: '#D32F2F',
  DARK_BG: '#1A1A1A',
  PAPER_BG: '#2D2D2D',
  SECONDARY: '#2A2F42',
  TEXT_SECONDARY: '#A0A0A0',
};

// Styled ToggleButton for Buy/Sell
const StyledToggleButton = styled(ToggleButton)(({ theme, value }) => ({
  backgroundColor: theme.palette.mode === 'dark' ? COLORS.SECONDARY : '#E0E0E0',
  color: theme.palette.text.secondary,
  '&.Mui-selected': {
    backgroundColor: value === 'Buy' ? COLORS.GREEN : COLORS.RED,
    color: theme.palette.text.primary,
    '&:hover': {
      backgroundColor: value === 'Buy' ? '#008A60' : '#B71C1C',
    },
  },
  '&:hover': {
    backgroundColor: value === 'Buy' ? '#008A60' : '#B71C1C',
  },
  borderRadius: value === 'Buy' ? '4px 0 0 4px' : '0 4px 4px 0',
  flex: 1,
  padding: '8px',
}));

// Styled TextField to hide arrows and apply yellow border on focus
const StyledTextField = styled(TextField)(({ theme }) => ({
  '& .MuiInputBase-root': {
    backgroundColor: theme.palette.mode === 'dark' ? COLORS.SECONDARY : '#F7F7F7',
    borderRadius: '4px',
  },
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.mode === 'dark' ? COLORS.SECONDARY : '#E0E0E0',
    },
    '&:hover fieldset': {
      borderColor: COLORS.YELLOW,
    },
    '&.Mui-focused fieldset': {
      borderColor: COLORS.YELLOW,
    },
  },
  '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
    WebkitAppearance: 'none',
    margin: 0,
  },
  '& input[type=number]': {
    MozAppearance: 'textfield',
  },
}));

const OrderForm = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { selectedCoin, loading: spotTradingLoading } = useSelector((state) => state.spotTrading);
  const { orderBook, spotBalances = [], spotTradingPairs } = useSelector((state) => state.assets);
  const user = useSelector((state) => state.user.data);

  const [tradeType, setTradeType] = useState('Buy');
  const [orderType, setOrderType] = useState('Market');
  const [limitPrice, setLimitPrice] = useState('');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [balancesLoading, setBalancesLoading] = useState(false);
  const [priceLoading, setPriceLoading] = useState(false);

  useEffect(() => {
    if (user?._id) {
      setBalancesLoading(true);
      dispatch(fetchSpotBalances({ forceRefresh: true }))
        .unwrap()
        .then(() => console.log('Spot balances fetched successfully'))
        .catch((err) => setError('Failed to fetch balances'))
        .finally(() => setBalancesLoading(false));
    }
  }, [dispatch, user?._id]);

  const tradingPair = useMemo(() => {
    return spotTradingPairs.find((pair) => pair.symbol === selectedCoin);
  }, [spotTradingPairs, selectedCoin]);

  const total = useMemo(() => {
    const price = orderType === 'Limit' ? parseFloat(limitPrice) || 0 : orderBook.currentPrice || 0;
    const qty = parseFloat(amount) || 0;
    return price * qty;
  }, [limitPrice, amount, orderType, orderBook.currentPrice]);

  const symbol = selectedCoin ? selectedCoin.replace(/USDT/i, '') : '';

  const baseAssetBalance =
    spotBalances.find((balance) => balance.asset_id?.symbol === symbol)?.balance || 0;
  const quoteAssetBalance =
    spotBalances.find((balance) => balance.asset_id?.symbol === 'USDT')?.balance || 0;

  // ✅ تحقق من وجود محافظ ورصيد
  const hasWallets = Array.isArray(spotBalances) && spotBalances.length > 0;
  const hasAnyBalance = baseAssetBalance > 0 || quoteAssetBalance > 0;

  const validateBalance = () => {
    if (tradeType === 'Buy') {
      if (total > quoteAssetBalance) {
        setError(`Insufficient balance: You only have ${quoteAssetBalance.toFixed(2)} USDT`);
        return false;
      }
    } else if (tradeType === 'Sell') {
      const amountValue = parseFloat(amount);
      if (isNaN(amountValue) || amountValue <= 0) {
        setError('Invalid amount');
        return false;
      }
      if (amountValue > baseAssetBalance) {
        setError(`Insufficient balance: You only have ${baseAssetBalance.toFixed(4)} ${symbol}`);
        return false;
      }
    }
    return true;
  };

  const handleRetryPriceFetch = async () => {
    if (!selectedCoin) return;
    setPriceLoading(true);
    setError(null);
    try {
      const response = await api.get(`/spot/price/${selectedCoin}`);
      console.log('Price fetched:', response.data);
      dispatch({
        type: 'assets/updateOrderBook',
        payload: { currentPrice: response.data.price },
      });
    } catch (err) {
      setError('Failed to fetch current price. Please try again.');
      console.error('Error fetching price:', err);
    } finally {
      setPriceLoading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setError(null);

    if (!user?._id) {
      setError('Please log in to create a trade order');
      return;
    }

    if (!selectedCoin || !tradingPair) {
      setError('Please select a valid trading pair');
      return;
    }

    if (!hasWallets) {
      setError('No spot wallet found. Please initialize your spot wallet before trading.');
      return;
    }

    if (!hasAnyBalance) {
      setError('Your balances are zero. Please deposit funds before placing an order.');
      return;
    }

    if (orderType === 'Market' && (!orderBook.currentPrice || orderBook.currentPrice === 0)) {
      setError('Current price not available. Please try again.');
      return;
    }

    if (!validateBalance()) return;

    setOpenConfirmDialog(true);
  };

  const confirmTrade = async () => {
    setIsSubmitting(true);
    const amountValue = parseFloat(amount);
    const limitPriceValue = orderType === 'Limit' ? parseFloat(limitPrice) : 0;

    if (isNaN(amountValue) || amountValue <= 0) {
      setError('Invalid amount');
      setIsSubmitting(false);
      setOpenConfirmDialog(false);
      return;
    }

    if (orderType === 'Limit' && (isNaN(limitPriceValue) || limitPriceValue <= 0)) {
      setError('Invalid limit price');
      setIsSubmitting(false);
      setOpenConfirmDialog(false);
      return;
    }

    const tradeData = {
      trading_pair_id: tradingPair._id,
      trade_type: tradeType,
      order_type: orderType,
      limit_price: limitPriceValue,
      amount: amountValue,
    };

    console.log('Submitting trade request with data:', tradeData);

    try {
      const response = await api.post(`/spot/trade`, tradeData);
      console.log('Order created successfully:', response.data);
      setError(null);
      setLimitPrice('');
      setAmount('');
      // Force refresh balances after trade
      const balances = await dispatch(fetchSpotBalances({ forceRefresh: true })).unwrap();
      console.log('Updated spot balances:', balances);
    } catch (err) {
      const errorMessage = err.response?.data?.message || 'Failed to create order';
      setError(errorMessage);
      console.error('Error creating trade:', err);
      console.error('Error details:', err.response?.data);
      if (err.response?.status === 401) {
        localStorage.removeItem('token');
        window.location.href = '/login';
      }
    } finally {
      setIsSubmitting(false);
      setOpenConfirmDialog(false);
    }
  };

  const handleTradeTypeChange = (e, newTradeType) => {
    if (newTradeType) setTradeType(newTradeType);
  };

  // ✅ منطق تعطيل زر Buy/Sell بالكامل
  const isTradeButtonDisabled = useMemo(() => {
    if (!hasWallets || !hasAnyBalance) return true;
    if (!amount) return true;
    if (orderType === 'Limit' && !limitPrice) return true;
    if (isSubmitting) return true;
    if (orderType === 'Market' && (!orderBook.currentPrice || orderBook.currentPrice === 0))
      return true;
    return false;
  }, [
    hasWallets,
    hasAnyBalance,
    amount,
    orderType,
    limitPrice,
    isSubmitting,
    orderBook.currentPrice,
  ]);

  if (!user?._id) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography sx={{ color: theme.palette.error.main }}>
          Please log in to create a trade order.
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => (window.location.href = '/login')}
          sx={{
            mt: 2,
            borderRadius: '4px',
            textTransform: 'none',
            bgcolor: COLORS.YELLOW,
            '&:hover': { bgcolor: '#D4A009' },
          }}
        >
          Go to Login
        </Button>
      </Box>
    );
  }

  if (balancesLoading) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ color: theme.palette.text.secondary, mt: 2 }}>
          Loading balances...
        </Typography>
      </Box>
    );
  }

  if (!selectedCoin || !tradingPair) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography sx={{ color: theme.palette.text.secondary }}>
          Please select a coin to create a trade order.
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        bgcolor: theme.palette.mode === 'dark' ? COLORS.PAPER_BG : '#FFFFFF',
        p: 2,
        borderRadius: 2,
        boxShadow:
          theme.palette.mode === 'dark'
            ? '0 2px 4px rgba(0, 0, 0, 0.5)'
            : '0 2px 4px rgba(0, 0, 0, 0.1)',
        maxWidth: 350,
        mx: 'auto',
      }}
    >
      <Typography variant="h6" sx={{ color: theme.palette.text.primary, mb: 2 }}>
        Trade Order - {selectedCoin}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography sx={{ color: theme.palette.text.secondary }}>
          Current Price:{' '}
          {orderBook.currentPrice ? `${orderBook.currentPrice.toFixed(2)} USDT` : 'Not available'}
        </Typography>
        {(!orderBook.currentPrice || orderBook.currentPrice === 0) && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleRetryPriceFetch}
            disabled={priceLoading}
            sx={{
              mt: 1,
              borderRadius: '4px',
              textTransform: 'none',
              color: COLORS.YELLOW,
              borderColor: COLORS.YELLOW,
              '&:hover': { borderColor: '#D4A009' },
            }}
          >
            {priceLoading ? <CircularProgress size={20} /> : 'Retry'}
          </Button>
        )}
      </Box>

      <Typography sx={{ color: theme.palette.text.secondary, mb: 1 }}>
        {symbol} Balance: {baseAssetBalance.toFixed(4)} | USDT Balance: {quoteAssetBalance.toFixed(2)}
      </Typography>

      {/* ✅ رسالة توضيحية عند عدم وجود محافظ أو رصيد */}
      {!hasWallets && (
        <Typography sx={{ color: theme.palette.error.main, mb: 1 }}>
          No spot wallet found. Please initialize your spot wallet before trading.
        </Typography>
      )}
      {hasWallets && !hasAnyBalance && (
        <Typography sx={{ color: theme.palette.error.main, mb: 1 }}>
          Your balances are zero. Please deposit funds to enable trading.
        </Typography>
      )}

      {error && (
        <Typography sx={{ color: theme.palette.error.main, mb: 2 }}>
          {error}
        </Typography>
      )}

      <form onSubmit={handleSubmit}>
        <ToggleButtonGroup
          value={tradeType}
          exclusive
          onChange={handleTradeTypeChange}
          sx={{ mb: 2, width: '100%' }}
        >
          <StyledToggleButton value="Buy">Buy</StyledToggleButton>
          <StyledToggleButton value="Sell">Sell</StyledToggleButton>
        </ToggleButtonGroup>

        <FormControl fullWidth sx={{ mb: 2 }}>
          <InputLabel sx={{ color: theme.palette.text.secondary }}>Order Type</InputLabel>
          <Select
            value={orderType}
            onChange={(e) => setOrderType(e.target.value)}
            label="Order Type"
            sx={{
              bgcolor: theme.palette.mode === 'dark' ? COLORS.SECONDARY : '#F7F7F7',
              borderRadius: '4px',
            }}
          >
            <MenuItem value="Market">Market</MenuItem>
            <MenuItem value="Limit">Limit</MenuItem>
          </Select>
        </FormControl>

        {orderType === 'Limit' && (
          <StyledTextField
            fullWidth
            label="Limit Price (USDT)"
            type="number"
            value={limitPrice}
            onChange={(e) => setLimitPrice(e.target.value)}
            sx={{ mb: 2 }}
            InputProps={{
              inputProps: { min: 0, step: 0.01 },
            }}
            InputLabelProps={{ sx: { color: theme.palette.text.secondary } }}
          />
        )}

        <StyledTextField
          fullWidth
          label={`Amount (${symbol})`}
          type="number"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          sx={{ mb: 2 }}
          InputProps={{
            inputProps: { min: 0, step: 0.0001 },
          }}
          InputLabelProps={{ sx: { color: theme.palette.text.secondary } }}
        />

        <BalanceSlider
          availableBalance={tradeType === 'Buy' ? quoteAssetBalance : baseAssetBalance}
          setAmount={setAmount}
          price={orderType === 'Limit' ? limitPrice : orderBook.currentPrice}
          tradeType={tradeType}
        />

        <Typography sx={{ color: theme.palette.text.secondary, mb: 2 }}>
          Total: {total.toFixed(2)} USDT
        </Typography>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isTradeButtonDisabled}
          sx={{
            bgcolor: tradeType === 'Buy' ? COLORS.GREEN : COLORS.RED,
            color: '#FFFFFF',
            '&:hover': {
              bgcolor: tradeType === 'Buy' ? '#008A60' : '#B71C1C',
            },
            py: 1,
            borderRadius: '4px',
            textTransform: 'none',
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} />
          ) : tradeType === 'Buy' ? (
            `Buy ${symbol}`
          ) : (
            `Sell ${symbol}`
          )}
        </Button>
      </form>

      <Dialog open={openConfirmDialog} onClose={() => setOpenConfirmDialog(false)}>
        <DialogTitle>Confirm Order</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to {tradeType.toLowerCase()} {amount} {symbol}?
          </Typography>
          {orderType === 'Limit' ? (
            <Typography>Limit Price: {limitPrice} USDT</Typography>
          ) : (
            <Typography>
              Current Price: {orderBook.currentPrice?.toFixed(2) || 'Not available'} USDT
            </Typography>
          )}
          <Typography>Total: {total.toFixed(2)} USDT</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setOpenConfirmDialog(false)}
            sx={{ color: COLORS.YELLOW, borderRadius: '4px', textTransform: 'none' }}
          >
            Cancel
          </Button>
          <Button
            onClick={confirmTrade}
            sx={{ color: COLORS.YELLOW, borderRadius: '4px', textTransform: 'none' }}
            disabled={isSubmitting}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(OrderForm);
